/**
 * Credits Service
 * Frontend service for credit operations via backend API
 */

import { getApiBase } from '../config/api';
import { authFetch } from '../utils/authFetch';

export interface UserSubscription {
  id: string;
  status: 'active' | 'trialing' | 'past_due' | 'cancelled' | 'paused';
  planName: string;
  planSlug: string;
  billingCycle: 'monthly' | 'annual';
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  monthlyCreditsAllocated: number;
  monthlyCreditsUsed: number;
  monthlyCreditsRemaining: number;
}

export interface UserCredits {
  balance: number;
  lifetimePurchased: number;
  lifetimeUsed: number;
  lifetimeRefunded: number;
  subscription?: UserSubscription | null;
}

export interface ModelCost {
  provider: string;
  modelId: string;
  displayName: string;
  operationType: string;
  creditsPerUnit: number;
  unit: 'generation' | 'second';
}

export interface CostCalculation {
  provider: string;
  modelId: string;
  displayName: string;
  creditsPerUnit: number;
  unit: string;
  units: number;
  totalCost: number;
}

export interface CreditTransaction {
  id: string;
  amount: number;
  type: 'reserve' | 'confirm' | 'refund' | 'purchase' | 'bonus';
  status: 'pending' | 'completed' | 'cancelled';
  description?: string;
  provider?: string;
  modelId?: string;
  operationType?: string;
  createdAt: string;
}

export interface CreditPackage {
  id: string;
  name: string;
  credits: number;
  bonusCredits: number;
  priceCents: number;
  isFeatured: boolean;
}

class CreditsService {
  private apiBase: string;

  constructor() {
    this.apiBase = getApiBase();
  }

  /**
   * Get user's credit balance
   */
  async getBalance(userId: string): Promise<UserCredits> {
    const response = await fetch(`${this.apiBase}/api/credits/balance`, {
      headers: {
        'x-user-id': userId,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get balance');
    }

    return response.json();
  }

  /**
   * Get transaction history
   */
  async getHistory(userId: string, limit = 50): Promise<CreditTransaction[]> {
    const response = await fetch(`${this.apiBase}/api/credits/history?limit=${limit}`, {
      headers: {
        'x-user-id': userId,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get history');
    }

    const data = await response.json();
    return data.transactions;
  }

  /**
   * Calculate cost for an operation
   */
  async calculateCost(provider: string, modelId: string, units = 1): Promise<CostCalculation> {
    const response = await fetch(`${this.apiBase}/api/credits/calculate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ provider, modelId, units }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to calculate cost');
    }

    return response.json();
  }

  /**
   * Get available models with pricing
   */
  async getModels(operationType?: string): Promise<ModelCost[]> {
    const url = operationType 
      ? `${this.apiBase}/api/models?operation=${operationType}`
      : `${this.apiBase}/api/models`;
      
    const response = await fetch(url);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get models');
    }

    const data = await response.json();
    return data.models;
  }

  /**
   * Get all model pricing from the database (model_registry).
   * Used to populate the frontend cost estimation cache.
   */
  async getPricing(): Promise<ModelCost[]> {
    const response = await fetch(`${this.apiBase}/api/credits/pricing`);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get pricing');
    }

    const data = await response.json();
    return data.models;
  }

  /**
   * Get available credit packages
   */
  async getPackages(): Promise<CreditPackage[]> {
    const response = await fetch(`${this.apiBase}/api/credits/packages`);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get packages');
    }

    const data = await response.json();
    return data.packages;
  }

  /**
   * Claim the tutorial completion refund.
   *
   * Refunds the credits the user spent on the generation they did inside
   * the named tutorial. Idempotent server-side — calling twice just returns
   * `alreadyRefunded: true` the second time.
   *
   * Fire-and-forget from TutorialContext on completion. Failures are logged
   * but don't block the user.
   */
  async claimTutorialRefund(tutorialId: string): Promise<{
    refunded: number;
    alreadyRefunded: boolean;
  }> {
    const response = await authFetch(`${this.apiBase}/api/credits/tutorial-refund`, {
      method: 'POST',
      body: JSON.stringify({ tutorialId }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || `Tutorial refund failed: ${response.status}`);
    }

    return response.json();
  }
}

export const creditsService = new CreditsService();
export default creditsService;
